import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CastMember } from '@core/classes/cast-member';
import { AUTH_ERROR_STATUS } from '@core/enums/auth-error-status';
import { Permissions } from '@core/enums/permissions.enum';
import { Sites } from '@core/enums/sites.enum';
import { SecuredEntitites } from '@core/enums/secured-entities.enum';
import { PermissionCheck } from '@core/interfaces/api/auth/permission-check.interface';
import { ValidateAuthResponse } from '@interfaces/api/auth/validate-auth-response.interface';
import { ClaimService } from '@services/claims/claims.service';
import { ConfigService } from '@services/config/config.service';
import { StorageService } from '@services/storage/storage.service';
import { UserAccessService } from '@services/user-access/user-access.service';
import { WindowRefService } from '@services/window-ref/window-ref.service';
import { LoggerService } from '@services/logger/logger.service';
import { jwtDecode } from 'jwt-decode';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { Store } from '@ngrx/store';
import { State } from '@shared/store/reducers';
import { userActions } from '@shared/store/actions';
import { keys, LocalStorageService } from '../storage/local-storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  public castmember: CastMember;
  public decodedUserIdToken: any;
  public onBehalfOf: string;
  private userTokenUpdatedSubject = new Subject<void>();
  public claims: ValidateAuthResponse;
  public userIdToken: string;

  /* istanbul ignore next */
  private readonly userIdTokenKey = environment.production ? 'user-id-token' : 'user-id-token-dev';

  constructor(
    private store: Store<State>,
    private configService: ConfigService,
    private http: HttpClient,
    private claimService: ClaimService,
    private loggerService: LoggerService,
    private router: Router,
    private storageService: StorageService,
    private userAccessService: UserAccessService,
    private windowRef: WindowRefService,
    private localService: LocalStorageService
  ) {
  }

  /**
   */
  /* istanbul ignore next */
  public isLoggedIn(): boolean {
    return true;
  }

  /**
   */
  /* istanbul ignore next */
  public getClaims() {
    return this.claims;
  }

  /**
   */
  public handleServiceError(err) {
    const unauthorizedError = AUTH_ERROR_STATUS.UNAUTHORIZED;
    const forbiddenError = AUTH_ERROR_STATUS.FORBIDDEN;
    const errors = [unauthorizedError, forbiddenError];
    /* istanbul ignore next */
    if (errors.indexOf(err.status) >= 0 || (err.networkError && err.networkError.statusCode === unauthorizedError)) {
      this.login();
    }
  }

  /**
   */
  /* istanbul ignore next */
  public login() {
    try {
      this.loggerService.log('User attempting to login');
      const isLocal = this.windowRef.nativeWindow.location.href.includes('localhost');
      const myIdBaseUrl = this.configService.getValue('authConfig').myIdBaseUrl;
      const authApiUrl = isLocal ? 'https://localhost:3000' : this.configService.getValue('baseHref');
      const apiUrl = this.configService.getValue('apiUrl');
      const originalRequestUrl = encodeURIComponent(this.windowRef.nativeWindow.location.href);
      const verifierCode = this.generateCodeVerifier();
      this.setCodeVerifyCookie(verifierCode);
      this.windowRef.nativeWindow.location.href = `${authApiUrl}${apiUrl}/${myIdBaseUrl}${isLocal ? '?islocal=true&' : '?'}redirectUri=${originalRequestUrl}`;
    } catch {
      this.router.navigateByUrl(`/access-error/403`);
    }
  }

  //Acme Changes start here
  public generateCodeVerifier(length = 64): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    for (let i = 0; i < length; i++) {
      result += charset[array[i] % charset.length];
    }
    return result;
  }

  public setCodeVerifyCookie(value:string) {
    const expiry = new Date(Date.now() + 120 * 1000).toUTCString();
    document.cookie = `codeVerifier=${encodeURIComponent(value)}; expires=${expiry}; path=/ `;
  }
  //Acme Changes end here

  /**
   * @param  {boolean} explicit?
   */
  /* istanbul ignore next */
  public logout(explicit?: boolean) {
    this.http.post(`api/auth/logout`, null).pipe(map(res => {
      this.loggerService.log('User logout successfully', res);

      return res;
    }))
      .subscribe(res => {
        if (explicit) {
          this.login();
        }

        return res;
      }, _error => {
        if (explicit) {
          this.login();
        }
      });
  }

  /**
   * validate token
   */
  /* istanbul ignore next */
  public validateToken() {
    this.loggerService.log('Calling validate token');


    /* istanbul ignore next */
    return this.claimService.getClaims()
      .pipe(map(result => {
        this.castmember = new CastMember(result);
        this.storageService.setSession('user', this.castmember);
        this.storeUserIdToken(result['token']);
        this.claims = result;
        this.userAccessService.setUser(this.castmember);
        this.loggerService.log('User ' + this.castmember.hubId + ' setup is completed',
          this.castmember.name, this.castmember.roles, this.castmember.permissions);

        // Save user assigned sites
        this.localService.saveData(keys.userSites, this.castmember.sites);

        // Dispatch event
        this.store.dispatch(userActions.setCurrUser(this.castmember));

        return result;
      }));
  }

  /**
   */
  public async getUserIdToken() {
    /* istanbul ignore next */
    if (!this.castmember.decodedToken) {
      this.decodedUserIdToken = (await this.storageService.getSession(this.userIdTokenKey));
    }

    return this.userIdToken;
  }

  /**
   */
  public async storeUserIdToken(token: string) {
    this.userIdToken = token;
    this.decodedUserIdToken = jwtDecode(token);
    this.onBehalfOf = this.decodedUserIdToken.sub;
    this.userTokenUpdatedSubject.next();

    return this.storageService.setSession(this.userIdTokenKey, token);
  }

  /**
   * @param  {Permissions} permissions
   * @param  {Permissions} permToCheck
   */
  /* istanbul ignore next */
  private _checkPerm(permissions: Permissions, permToCheck: Permissions) {
       return (permissions & permToCheck) === permToCheck;
  }

  /**
   * @param  {string} permissionName
   * @param  {Permissions} permission
   */
  /* istanbul ignore next */
  public hasPermission(permissionName: string, permission: Permissions) {
    if (this.decodedUserIdToken.permissions && this.decodedUserIdToken.permissions[permissionName]) {
      const sites = this.decodedUserIdToken.permissions[permissionName].site;

      return _.find(Object.values(sites as Permissions), (perm => this._checkPerm(perm, permission) == true));
    }

    return false;
  }

  /**
   * @param  {SecuredEntitites} securedEntity
   * @param  {Permissions} permission
   * @param  {Sites|string} site
   */
  /* istanbul ignore next */
  public hasSitePerission(securedEntity: SecuredEntitites, permission: Permissions, site: Sites | string) {
    if (this.decodedUserIdToken.permissions && this.decodedUserIdToken.permissions[securedEntity] &&
      this.decodedUserIdToken.permissions[securedEntity].site && this.decodedUserIdToken.permissions[securedEntity].site[site]) {
      return this._checkPerm(this.decodedUserIdToken.permissions[securedEntity].site[site], permission);
    }

    return false;
  }

  /**
   */
  public async getDecodedUserIdToken() {
    /* istanbul ignore else */
    if (this.decodedUserIdToken == null) {
      const decodedToken = await this.storageService.getSession(this.userIdTokenKey);
      this.decodedUserIdToken = jwtDecode(decodedToken);
    }
  }

  /**
   * @param  {string=Roles.Admin} roleName
   */
  /* istanbul ignore next */
  public hasRole(roleName: string) {

    this.getDecodedUserIdToken();
    const decodedToken = this.decodedUserIdToken;
    const roles = _.get(decodedToken, 'rolesName');

    if (!(decodedToken && roles)) {
      return false;
    }

    return _.includes(roles, roleName);
  }

  /**
   * @param  {string[]} roles
   */
  /* istanbul ignore next */
  public hasAnyRole(roles: string[]) {
    for (const role of roles) {
      /* istanbul ignore else */
      if (this.hasRole(role)) {
        return true;
      }
    }

    return false;
  }

  /**
   * @param  {string} abilityName
   */
  public hasAbility(abilityName: string) {
    this.getDecodedUserIdToken();
    const decodedToken = this.decodedUserIdToken;
    const abilitiesObj = _.get(decodedToken, 'functionalAbilities');
    const allowedAbilities = _.keys(_.pickBy(abilitiesObj, _.identity));

    if (!(decodedToken && allowedAbilities)) {
      return false;
    }

    return _.includes(allowedAbilities, abilityName);
  }

  /**
   * @param  {string[]} abilities
   */
  public hasAnyAbility(abilities: string[]) {
    if (!_.isEmpty(abilities)) {
      for (const ability of abilities) {
        return this.hasAbility(ability);
      }
    }

    return false;
  }

  /**
   * @param  {PermissionCheck[]} permissions
   */
  /* istanbul ignore next */
  public hasAnyPermission(permissions: PermissionCheck[]) {
    for (const permission of permissions) {
      if (this.hasPermission(permission.name, permission.permission)) {
        return true;
      }
    }

    return false;
  }

  public getUserAbilities(): string[] {
    this.getDecodedUserIdToken();
    const decodedToken = this.decodedUserIdToken;
    const abilitiesObj = _.get(decodedToken, 'functionalAbilities');

    return _.keys(_.pickBy(abilitiesObj, _.identity));
  }
}
